import { useNavigate, useParams } from 'react-router-dom';

import { Skeleton, Typography } from 'antd';

import BreadCrumbs from '@/components/shared/BreadCrumbs';
import CourseAlerts from '@/components/shared/NegativeAlerts/Course';
import OfferCard from '@/components/shared/OfferCard';
import PageBackgroundImage from '@/components/shared/PageBackgroundImage';
import ProgressStatus from '@/components/shared/ProgressStatus';
import Wrapper from '@/components/shared/Wrapper';
import { useGetCourseQuery } from '@/services/course';
import styled, { css, StyledProp } from '@/styled';

import Author from './Author';
import Integrations from './Integrations';
import KeepLearning from './KeepLearning';
import CourseTabs from './Tabs';
import CourseTerm from './Term';

const Course: React.FC = (props: StyledProp) => {
  const { hash } = useParams();
  const { data, isFetching } = useGetCourseQuery(hash);
  const navigate = useNavigate();

  const crumbs = [
    {
      title: 'Resumo',
      onClick: () => navigate('/resumo')
    },
    {
      title: 'Meus Cursos',
      onClick: () => navigate('/cursos')
    },
    {
      title: (
        <Typography.Text ellipsis className='courseTitle'>
          {data?.title}
        </Typography.Text>
      )
    }
  ];

  return (
    <div className={props.className}>
      <CourseTerm />
      <PageBackgroundImage
        src={data?.customizations.course_newcard_background || data?.customizations?.image_cover}
        alt='banner de fundo da tela de curso'
      />

      <Wrapper className='wrapper'>
        {!isFetching && <BreadCrumbs items={crumbs} />}
        <Typography.Title level={1} ellipsis className='title'>
          {data?.title ?? <Skeleton.Input size='small' />}
        </Typography.Title>
        <ProgressStatus className='progress' />
        <KeepLearning courseStatus={data?.status} />
        <CourseAlerts />
        <div className='content'>
          <CourseTabs />
          <div className='side-content'>
            <Integrations />
            <Author />
            <OfferCard />
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default styled(Course)(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    min-height: 100vh;
    > .wrapper {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;

      .courseTitle {
        max-width: 500px;
        color: ${theme.colorTextBase};
      }

      .ant-skeleton {
        width: 100%;
      }
      .progress {
        margin-bottom: ${theme.space.pxToRem(64)};
      }

      .ant-skeleton {
        border-radius: ${theme.borderRadius}px;
        height: fit-content !important;
      }

      h1.ant-typography {
        margin-top: ${theme.space.pxToRem(74)};
        margin-bottom: ${theme.space.pxToRem(20)};
      }
    }

    .content {
      display: grid;
      margin-top: ${theme.space.pxToRem(64)};
      grid-template-columns: auto 308px;
      max-width: 100%;
      position: relative;
      overflow: hidden;

      .ant-tabs {
        width: 100%;
        max-width: 100%;
        overflow: hidden;
      }

      ${theme.breakpoints.down('md')} {
        grid-template-columns: auto;
        grid-row-gap: ${theme.space.pxToRem(64)};
      }
    }

    .side-content {
      display: flex;
      flex-direction: column;
      gap: ${theme.space.pxToRem(24)};
    }
  `
);
